import { PlusOutlined } from '@ant-design/icons'
import useDailyTasks, {
  TempCalculatedTaskStatus
} from '@features/todos/hooks/useDailyTasks'
import type { AllFilters } from '@features/todos/utils/dashboardFilters'
import { ADD_TODO_HASH_ROUTE } from '@features/todos/utils/managerUtils'
import type { CalculatedTaskStatus, Day, Home } from '@lmc/ts-common'
import { Button, StatusBadge } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import { TODOS } from 'components/routes/todos'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import qs from 'qs'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { toDosConstants } from '../../../locale/toDos'
import { EmptyItems, HomeCard } from './index'

const today = dayjs()

type Props = {
  hasPro?: boolean
}

const ToDosCard: FC<Props> = ({ hasPro }) => {
  const { days, isLoading } = useDailyTasks({
    rangePicker: [today, today]
  })

  const { t } = useTranslation(NAMESPACE.TODOS)

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const day = days[0] as Day | undefined
  const totalTasks = getTotalTodos(day?.counts)

  const canAddTodo = useAbilityAccess([{ i: 'create', this: 'TaskManagement' }])

  const addTodoButton = (
    <Button
      icon={<PlusOutlined />}
      variant="primary"
      disabledByPermission={!canAddTodo}
    >
      {t(toDosConstants.ADD_TO_DO)}
    </Button>
  )

  return (
    <HomeCard
      title={t(toDosConstants.TODAYS_TODOS)}
      titleLink={TODOS.DASHBOARD}
      footer={
        <Buttons>
          {canAddTodo ? (
            <Link to={`${TODOS.DASHBOARD}#${ADD_TODO_HASH_ROUTE}`}>
              {addTodoButton}
            </Link>
          ) : (
            addTodoButton
          )}
        </Buttons>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : totalTasks > 0 ? (
        <Content>
          <Text weight="bold">{`${totalTasks} `}</Text>
          <Text>{t(toDosConstants.TODOS_FOR_TODAY)}</Text>
          <StatusesContainer>
            {Object.keys(TempCalculatedTaskStatus)?.map((statusType) => (
              <Link
                key={statusType}
                to={`${TODOS.DASHBOARD}?${getDashboardQueryParams(
                  statusType as CalculatedTaskStatus
                )}`}
              >
                <StatusBadge
                  key={statusType}
                  status={statusType as CalculatedTaskStatus}
                  count={day?.counts[statusType as CalculatedTaskStatus] || 0}
                  leftText={false}
                  showStatus
                />
              </Link>
            ))}
          </StatusesContainer>
        </Content>
      ) : (
        <EmptyStateWrapper>
          <EmptyItems size="sm" items={4} />
          {hasPro && (
            <InfoWrapper>
              <StyledText>
                {t(toDosConstants.CREATE_A_TODO, {
                  variable: preferredPeopleNominative
                })}
              </StyledText>
              <HereWrapper>
                <StyledText>
                  {t(toDosConstants.SEE_TODOS)}
                  <Link to={TODOS.DASHBOARD}>
                    <StyledTextHere>{t(toDosConstants.HERE)}</StyledTextHere>
                  </Link>
                </StyledText>
              </HereWrapper>
            </InfoWrapper>
          )}
        </EmptyStateWrapper>
      )}
    </HomeCard>
  )
}

export const getDashboardQueryParams = (
  status: CalculatedTaskStatus
): string => {
  const filter: Required<Pick<AllFilters, 'status'>> & {
    rangePicker: [string, string]
  } = {
    rangePicker: [today.toISOString(), today.toISOString()],
    status: [status]
  }

  return qs.stringify(filter)
}

const getTotalTodos = (counts: Day['counts'] | undefined): number => {
  if (!counts) {
    return 0
  }
  let total = 0

  Object.keys(counts).forEach((status) => {
    total += counts?.[status as keyof typeof counts] || 0
  })

  return total
}

const EmptyStateWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const Content = styled.div`
  min-height: fit-content;
`
const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`
const StatusesContainer = styled.div`
  margin-top: 16px;
  width: fit-content;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 48px;
  row-gap: 8px;
  > * {
    width: fit-content;
  }
`

const InfoWrapper = styled.div`
  background-color: #fff8fa;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  max-width: 240px;
  display: flex;
  flex-direction: column;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledTextHere = styled(Text)`
  color: ${({ theme }) => theme.color.status.popout};
  text-decoration: underline;
  cursor: pointer;
`

const HereWrapper = styled.div`
  display: flex;
  margin-top: 20px;
`

export default ToDosCard
